import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Shield, ChevronRight, Loader2, AlertCircle, User } from 'lucide-react';
import { tradingApi } from '../../services/tradingApi';
import type { RbacUserSummary } from '../../types/trading';

const TIER_BADGE: Record<string, string> = {
  free: 'bg-slate-700 text-slate-300',
  pro: 'bg-blue-900/40 text-blue-300',
  premium: 'bg-purple-900/40 text-purple-300',
};

export default function RbacUsers() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [users, setUsers] = useState<RbacUserSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  const search = async (q: string, newSkip = 0) => {
    setLoading(true);
    setError(null);
    try {
      const data = await tradingApi.getRbacUsers({ q: q || undefined, skip: newSkip, limit });
      setUsers(data);
      setSkip(newSkip);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery(inputValue);
    search(inputValue, 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-indigo-900/30 rounded-lg">
          <Shield className="h-5 w-5 text-indigo-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-white">Access Control</h1>
          <p className="text-sm text-slate-400">Manage user roles and permission overrides</p>
        </div>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Search by email or name…"
            className="w-full pl-9 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors"
        >
          Search
        </button>
      </form>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800 rounded-lg text-red-400 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 text-indigo-400 animate-spin" />
        </div>
      ) : users.length === 0 && query ? (
        <div className="text-center py-12 text-slate-500">
          <User className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>No users found for "{query}"</p>
        </div>
      ) : users.length > 0 ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 text-slate-400 text-xs uppercase">
                <th className="text-left px-4 py-3 font-medium">User</th>
                <th className="text-left px-4 py-3 font-medium">Tier</th>
                <th className="text-left px-4 py-3 font-medium">Roles</th>
                <th className="text-left px-4 py-3 font-medium">Overrides</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="hover:bg-slate-700/30 cursor-pointer transition-colors"
                  onClick={() => navigate(`/admin/access-control/users/${user.id}`)}
                >
                  <td className="px-4 py-3">
                    <p className="text-white font-medium">{user.email}</p>
                    {user.full_name && (
                      <p className="text-slate-400 text-xs">{user.full_name}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        TIER_BADGE[user.subscription_tier] ?? TIER_BADGE.free
                      }`}
                    >
                      {user.subscription_tier}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {user.roles.length === 0 ? (
                        <span className="text-slate-500 text-xs">—</span>
                      ) : (
                        user.roles.map((r) => (
                          <span
                            key={r.name}
                            className="px-1.5 py-0.5 bg-slate-700 text-slate-300 rounded text-xs"
                          >
                            {r.name}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    {user.override_count > 0 ? (
                      <span className="px-2 py-0.5 bg-amber-900/30 text-amber-400 rounded-full text-xs">
                        {user.override_count}
                      </span>
                    ) : (
                      <span className="text-slate-500 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    <ChevronRight className="h-4 w-4" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-slate-700 flex items-center justify-between text-sm text-slate-400">
            <span>
              Showing {skip + 1}–{skip + users.length}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => search(query, Math.max(0, skip - limit))}
                disabled={skip === 0}
                className="px-3 py-1 rounded border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <button
                onClick={() => search(query, skip + limit)}
                disabled={users.length < limit}
                className="px-3 py-1 rounded border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-16 text-slate-500">
          <Shield className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="font-medium text-slate-400">Search for a user to manage their access</p>
          <p className="text-xs mt-1">Enter an email or name to get started</p>
        </div>
      )}
    </div>
  );
}
